import { useTextes } from '@common/config';
import { Z_SIRET } from '@common/schemas/src/etablissement/siret.schema';
import {
  EtablissementRuleInfoDto,
  NdaCodeDto,
  useLazyGetEtablissementRuleInfoQuery,
  useLazyGetNdasBySirenAndSiretQuery,
  useLazyIsSiretAvailableQuery,
} from '@common/services';
import { PrefixedTextField } from '@common/ui';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCheckFormSchema } from './hooks/use-check-form-schema.hook';
import { useFormFieldParams } from './hooks/use-form-field-params.hook';

// TODO Envoyer ces messages erreurs depuis le serveur
export const SIRET_ICONNU_MESSAGE = 'Le SIRET n\'est pas connu, veuillez contacter France compétences';
export const SIRET_DEJA_UTILISE_MESSAGE = 'Ce SIRET est déjà utilisé par un autre établissement de l\'organisme';

export const SiretFormField = forwardRef<SiretFormFieldHandle, SiretFormFieldProps>(({
                                                                                       isAddMode,
                                                                                       organismeId,
                                                                                       siren,
                                                                                       onRuleInfoChange,
                                                                                       onSiretValidChange,
                                                                                       onNdasChange,
                                                                                     }, ref) => {
  useCheckFormSchema({ name: 'siret' });

  const t = useTextes('Commun', 'Formulaire');
  const { setError: setFormError, clearErrors } = useFormContext();
  const { currentValue: siret, setValue: setSiret } = useFormFieldParams({ name: 'siret' });

  const [previousSiretErrorMessage, setPreviousSiretErrorMessage] = useState<string>(null);

  const [getIsSiretAvailable] = useLazyIsSiretAvailableQuery();
  const [getEtablissementRuleInfo] = useLazyGetEtablissementRuleInfoQuery();
  const [getNdasBySirenAndSiret] = useLazyGetNdasBySirenAndSiretQuery();

  // Exposer les méthodes que le parent peut appeler
  useImperativeHandle(ref, () => ({
    forceError: () => {
      // permet de forcer une erreur du champ siret car celui-ci est clear lors du submit
      if (previousSiretErrorMessage) {
        setFormError('siret', { type: 'manual', message: previousSiretErrorMessage });
      }
    },
  }));

  const handleSiretError = (message) => {
    setFormError('siret', { type: 'manual', message });
    setPreviousSiretErrorMessage(message);
    onSiretValidChange(false);
  };

  const validateSiret = () => {
    clearErrors('siret');
    onSiretValidChange(true);
  };

  const fetchPublicNdas = async () => {
    const ndaDtos: NdaCodeDto[] | null = await getNdasBySirenAndSiret({ siren, siret }).unwrap().catch(() => null); // En cas d'erreur, on considère qu'aucun NDA n'a été trouvé
    if (!ndaDtos) return;

    onNdasChange(ndaDtos);
  };

  const fetchRuleInfo = async () => {
    try {
      const infoDto: EtablissementRuleInfoDto = await getEtablissementRuleInfo({ siret }).unwrap();
      onRuleInfoChange(infoDto);
      validateSiret();
    } catch (err: unknown) {
      // siret invalide, car inconnu de Rule
      handleSiretError(SIRET_ICONNU_MESSAGE);
    }
  };

  const checkAvailabilityAndValidate = async () => {
    const isAvailable: boolean = await getIsSiretAvailable({ id: organismeId, siret }).unwrap();
    if (!isAvailable) return handleSiretError(SIRET_DEJA_UTILISE_MESSAGE);

    await Promise.all([
      fetchRuleInfo(),
      fetchPublicNdas(),
    ]);
  };

  useEffect(() => {
    if (!isAddMode || !Z_SIRET.safeParse(siret).success) return;

    // on vérifie si le siret n'est pas utilisé par un autre etablissement et valide le siret
    checkAvailabilityAndValidate().then();
  }, [siret]);

  return (
    <PrefixedTextField name="siret"
                       title="SIRET"
                       prefix={siren ?? ''}
                       maxDigits={5}
                       value={siret}
                       onChange={setSiret}
                       tooltip={t['DONNEE_OFA_ETAB_SIRET_INFO_BULLE_MSG']} />
  );
});

export interface SiretFormFieldHandle {
  forceError: () => void;
}

interface SiretFormFieldProps {
  organismeId: number,
  siren: string,
  isAddMode: boolean,
  onSiretValidChange: (valid: boolean) => void,
  onRuleInfoChange: (info: EtablissementRuleInfoDto) => void,
  onNdasChange: (ndas: NdaCodeDto[]) => void;
}